import React from 'react'
import {
  FaStar,
  FaCodeBranch,
  FaEye,
  FaExclamationCircle,
  FaCode,
  FaUsers,
  FaCalendar,
  FaGithub,
  FaCheckCircle,
} from 'react-icons/fa'

/**
 * GitHubMetadata Component
 * 
 * Displays comprehensive GitHub repository metadata including:
 * - Repository stats (stars, forks, watchers, issues)
 * - Commit count and contributors
 * - Language breakdown with percentages
 * - Last update timestamp
 * - Recent commits
 */
const GitHubMetadata = ({ metadata, showTitle = true }) => {
  if (!metadata || !metadata.verified) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <FaExclamationCircle className="w-5 h-5" />
          <p className="font-medium">Repository Not Verified</p>
        </div>
        {metadata?.error && (
          <p className="text-sm text-red-600 mt-2">{metadata.error}</p>
        )}
      </div>
    )
  }

  const stats = metadata.stats
  const languages = metadata.languages || []
  const recentCommits = metadata.recent_commits || []

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Get language color (simplified version)
  const getLanguageColor = (lang) => {
    const colors = {
      JavaScript: '#f7df1e',
      TypeScript: '#3178c6',
      Python: '#3572A5',
      Java: '#b07219',
      Ruby: '#CC342D',
      Go: '#00ADD8',
      Rust: '#dea584',
      PHP: '#777BB4',
      C: '#555555',
      'C++': '#f34b7d',
      'C#': '#178600',
      Swift: '#ffac45',
      Kotlin: '#A97BFF',
      Dart: '#00B4AB',
      HTML: '#e34c26',
      CSS: '#563d7c',
      Vue: '#41b883',
      React: '#61dafb',
    }
    return colors[lang] || '#8B949E'
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-6 space-y-6">
      {/* Header */}
      {showTitle && (
        <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
          <div className="p-2 bg-green-100 rounded-lg">
            <FaCheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Repository Verified</h3>
            <p className="text-sm text-gray-600">
              Metadata fetched {formatDate(metadata.verified_at)}
            </p>
          </div>
        </div>
      )}

      {/* Repository Info */}
      {stats && (
        <>
          {/* Repository Name & Description */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <FaGithub className="w-5 h-5 text-gray-700" />
              <a
                href={stats.html_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xl font-bold text-blue-600 hover:text-blue-700 hover:underline"
              >
                {stats.full_name}
              </a>
            </div>
            {stats.description && (
              <p className="text-gray-700 mb-3">{stats.description}</p>
            )}
            
            {/* Topics/Tags */}
            {stats.topics && stats.topics.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {stats.topics.map((topic, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Stars */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
              <FaStar className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats.stars || 0}</p>
              <p className="text-sm text-gray-600">Stars</p>
            </div>

            {/* Forks */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
              <FaCodeBranch className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats.forks || 0}</p>
              <p className="text-sm text-gray-600">Forks</p>
            </div>

            {/* Watchers */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
              <FaEye className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats.watchers || 0}</p>
              <p className="text-sm text-gray-600">Watchers</p>
            </div>

            {/* Issues */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
              <FaExclamationCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats.open_issues || 0}</p>
              <p className="text-sm text-gray-600">Issues</p>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Commits */}
            {stats.commits_count > 0 && (
              <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-4">
                <FaCode className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="text-lg font-bold text-gray-900">{stats.commits_count}</p>
                  <p className="text-sm text-gray-600">Commits</p>
                </div>
              </div>
            )}

            {/* Contributors */}
            {stats.contributors_count > 0 && (
              <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-4">
                <FaUsers className="w-5 h-5 text-indigo-500" />
                <div>
                  <p className="text-lg font-bold text-gray-900">{stats.contributors_count}</p>
                  <p className="text-sm text-gray-600">Contributors</p>
                </div>
              </div>
            )}

            {/* Last Update */}
            <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-4">
              <FaCalendar className="w-5 h-5 text-orange-500" />
              <div>
                <p className="text-sm font-semibold text-gray-900">{formatDate(stats.pushed_at)}</p>
                <p className="text-sm text-gray-600">Last Updated</p>
              </div>
            </div>
          </div>

          {/* License */}
          {stats.license && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">License:</span> {stats.license}
              </p>
            </div>
          )}
        </>
      )}

      {/* Language Breakdown */}
      {languages && languages.length > 0 && (
        <div>
          <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FaCode className="w-4 h-4" />
            Language Breakdown
          </h4>
          <div className="space-y-2">
            {languages.slice(0, 5).map((lang, idx) => (
              <div key={idx}>
                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getLanguageColor(lang.name) }}
                    />
                    <span className="text-sm font-medium text-gray-700">{lang.name}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {lang.percentage}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${lang.percentage}%`,
                      backgroundColor: getLanguageColor(lang.name),
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Commits */}
      {recentCommits && recentCommits.length > 0 && (
        <div>
          <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FaGithub className="w-4 h-4" />
            Recent Commits
          </h4>
          <div className="space-y-2">
            {recentCommits.slice(0, 3).map((commit, idx) => (
              <a
                key={idx}
                href={commit.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <p className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
                  {commit.message.split('\n')[0]}
                </p>
                <div className="flex items-center gap-3 text-xs text-gray-600">
                  <span>{commit.author.name}</span>
                  <span>•</span>
                  <span>{formatDate(commit.author.date)}</span>
                  <span>•</span>
                  <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono">
                    {commit.sha.substring(0, 7)}
                  </code>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default GitHubMetadata
